import { ClaimWorkflowConfigurationOptions, CreateClaimWorkflowConfigurationRequest } from '../../api';
import {
    WorkerConfigurationApiMode,
    fetchClaimWorkflowConfiguration,
    postClaimWorkflowConfiguration,
} from './worker-configuration.api';
import {
    canEditWorkflowOption,
    isProviderRejectedWorkflowOption,
} from './worker-configuration.permissions';
import { stableStringify } from './stable-stringify';
import {
    cloneWorkflowConfigMap,
    coerceNumericWorkflowConfigValues,
    parseWorkerWorkflowConfiguration,
    WorkerWorkflowConfiguration,
    WorkflowConfigMap,
} from './worker-configuration.types';

type ClaimWorkflowConfigurationRequestBody = Record<string, Record<string, unknown>>;

function hasPersistedConfiguration(
    configuration: WorkerWorkflowConfiguration | null,
    savedConfig: WorkflowConfigMap,
): boolean {
    return Boolean(configuration?.createDate) || Object.keys(savedConfig).length > 0;
}

export { hasPersistedConfiguration };

/**
 * Provider must not write support-only options, but a full replace would clear them if omitted.
 * Strip provider-uneditable keys from draft, then restore their saved values into the body —
 * except the ones the provider endpoint rejects on presence, which stay out entirely.
 */
function buildSaveRequestBody(
    draftConfig: WorkflowConfigMap,
    savedConfig: WorkflowConfigMap,
    apiMode: WorkerConfigurationApiMode,
): ClaimWorkflowConfigurationRequestBody {
    if (apiMode !== 'provider') {
        return draftConfig as ClaimWorkflowConfigurationRequestBody;
    }

    const next = cloneWorkflowConfigMap(draftConfig);

    for (const [status, statusConfig] of Object.entries(next)) {
        for (const key of Object.keys(statusConfig)) {
            if (!canEditWorkflowOption('provider', status, key)) {
                delete statusConfig[key];
            }
        }
    }

    for (const [status, statusConfig] of Object.entries(savedConfig)) {
        for (const [key, value] of Object.entries(statusConfig)) {
            if (canEditWorkflowOption('provider', status, key)) {
                continue;
            }

            if (isProviderRejectedWorkflowOption(status, key)) {
                continue;
            }

            if (!next[status]) {
                next[status] = {};
            }
            next[status][key] = value;
        }
    }

    for (const [status, statusConfig] of Object.entries(next)) {
        if (Object.keys(statusConfig).length === 0) {
            delete next[status];
        }
    }

    return next as ClaimWorkflowConfigurationRequestBody;
}

/**
 * Saves the full workflow configuration in one request via create-or-replace POST.
 * Body shape: { [workflowStatus]: { [optionKey]: value, rules?: Rule[] } }
 */
export async function saveClaimWorkflowConfiguration(
    draftConfig: WorkflowConfigMap,
    savedConfig: WorkflowConfigMap,
    configuration: WorkerWorkflowConfiguration | null,
    apiMode: WorkerConfigurationApiMode = 'support',
    optionsList: ClaimWorkflowConfigurationOptions[] = [],
): Promise<WorkerWorkflowConfiguration> {
    const typedDraft = coerceNumericWorkflowConfigValues(draftConfig, optionsList);
    const typedSaved = coerceNumericWorkflowConfigValues(savedConfig, optionsList);

    if (
        hasPersistedConfiguration(configuration, typedSaved) &&
        stableStringify(typedDraft) === stableStringify(typedSaved)
    ) {
        const latest = await fetchClaimWorkflowConfiguration(apiMode);
        return parseWorkerWorkflowConfiguration(latest);
    }

    const requestBody = coerceNumericWorkflowConfigValues(
        buildSaveRequestBody(typedDraft, typedSaved, apiMode) as WorkflowConfigMap,
        optionsList,
    );
    const saved = await postClaimWorkflowConfiguration(
        apiMode,
        requestBody as CreateClaimWorkflowConfigurationRequest['requestBody'],
    );

    return parseWorkerWorkflowConfiguration(saved);
}

export function createEmptyWorkflowConfigMap(): WorkflowConfigMap {
    return cloneWorkflowConfigMap();
}
